import SBRebuy from "./sb-rebuy.js";

if (!customElements.get("sb-featured-products")) {
  class SBFeaturedProducts extends HTMLElement {
    constructor() {
      super();
      this.swiper = null;
      this.init = this.init.bind(this);
      this.destroy = this.destroy.bind(this);
      this.enableSlider = this.dataset.enableSlider === "true" || false;
      this.rebuyEnabled = this.dataset.rebuyEnabled === "true" || false;
      this.rebuyProductType = this.dataset?.rebuyProductType;
      this.rebuyCustomEndpointId = this.dataset?.rebuyCustomEndpointId || false;
      this.cartItem = this.dataset?.cartItem || false;
      this.productLimit = parseInt(this.dataset?.productLimit);
      this.rebuyDynamicProduct =
        this.dataset?.rebuyDynamicProduct == "false" ? false : parseInt(this.dataset?.rebuyDynamicProduct);
      this.customerId = this.dataset?.customerId == "false" ? false : parseInt(this.dataset?.customerId);
      this.desktopWidthAuto = this.dataset?.desktopWidthAuto === "true" || false;
      this.desktopHeightAuto = this.dataset?.desktopHeightAuto === "true" || false;
      this.bannerHeightObserver = null;
      this.desktopMql = null;
      this.applyBannerHeight = null;
      this.heightClampObserver = null;
      this.heightClampMql = null;
      this.applyBannerHeightClamp = null;
    }

    connectedCallback() {
      if (this.rebuyEnabled) {
        this.fetchRebuyProducts();
      }

      if (this.enableSlider) {
        if (window.Swiper) {
          this.init();
        } else {
          const interval = setInterval(() => {
            if (window.Swiper) {
              clearInterval(interval);
              this.init();
            }
          }, 50);
        }
      }

      if (this.desktopWidthAuto) {
        this.initBannerHeightSync();
      }

      if (this.desktopHeightAuto) {
        this.initBannerHeightClamp();
      }
    }

    // Auto desktop-width banner: its width comes from its own image
    // aspect-ratio, but that only produces a definite width once it has a
    // definite height to derive from. CSS alone can't reliably give it "the
    // carousel's height, and never more" here — a plain flex/grid stretch
    // also has to size the box *before* stretching it, and a box with only
    // aspect-ratio and no other sizing info still gets a browser fallback
    // size at that point, which can end up larger than the real card
    // height and drag the whole row taller. Measuring the carousel and
    // setting the banner's height explicitly sidesteps that entirely.
    initBannerHeightSync() {
      const swiperOuter = this.closest(".sb-featured-products__swiper-outer");
      const row = this.closest(".sb-featured-products__row");
      const banner = row ? row.querySelector(".sb-featured-products__banner") : null;
      if (!swiperOuter || !banner || !window.ResizeObserver) return;

      this.desktopMql = window.matchMedia("(min-width: 750px)");
      this.applyBannerHeight = () => {
        banner.style.height = this.desktopMql.matches ? `${swiperOuter.offsetHeight}px` : "";
      };

      this.bannerHeightObserver = new ResizeObserver(this.applyBannerHeight);
      this.bannerHeightObserver.observe(swiperOuter);
      this.desktopMql.addEventListener("change", this.applyBannerHeight);
    }

    // Fixed-width, auto-height banner: the banner's CSS aspect-ratio (set
    // via liquid, from the image's own ratio) gives it a natural height at
    // its fixed grid-column width. Clamp that between the carousel's own
    // height and 1.5x that height, so a tall image can stretch the banner
    // beyond the card row without cropping, but not indefinitely. Only
    // swiperOuter is observed (never the banner itself, which this method
    // mutates) to avoid feeding back into its own ResizeObserver; swiperOuter
    // still resizes on ordinary window resizes since it's a fluid grid column.
    initBannerHeightClamp() {
      const swiperOuter = this.closest(".sb-featured-products__swiper-outer");
      const row = this.closest(".sb-featured-products__row");
      const banner = row ? row.querySelector(".sb-featured-products__banner") : null;
      if (!swiperOuter || !banner || !window.ResizeObserver) return;

      this.heightClampMql = window.matchMedia("(min-width: 750px)");
      this.applyBannerHeightClamp = () => {
        if (!this.heightClampMql.matches) {
          banner.style.height = "";
          return;
        }
        banner.style.height = "";
        const naturalHeight = banner.offsetHeight;
        const cardHeight = swiperOuter.offsetHeight;
        const clampedHeight = Math.min(Math.max(naturalHeight, cardHeight), cardHeight * 1.5);
        banner.style.height = `${clampedHeight}px`;
      };

      this.heightClampObserver = new ResizeObserver(this.applyBannerHeightClamp);
      this.heightClampObserver.observe(swiperOuter);
      this.heightClampMql.addEventListener("change", this.applyBannerHeightClamp);
    }

    async fetchRebuyProducts() {
      const rebuyInstance = new SBRebuy();
      rebuyInstance.initRebuyVariables(
        this.rebuyProductType,
        this.productLimit,
        this.rebuyDynamicProduct,
        this.customerId,
        this.rebuyCustomEndpointId,
        this.cartItem,
      );
      const products_handle = await rebuyInstance.fetchRebuyProducts();
      this.renderProducts(products_handle);
    }

    disconnectedCallback() {
      if (this.enableSlider) this.destroy();

      if (this.bannerHeightObserver) {
        this.bannerHeightObserver.disconnect();
        this.bannerHeightObserver = null;
      }
      if (this.desktopMql && this.applyBannerHeight) {
        this.desktopMql.removeEventListener("change", this.applyBannerHeight);
        this.desktopMql = null;
        this.applyBannerHeight = null;
      }

      if (this.heightClampObserver) {
        this.heightClampObserver.disconnect();
        this.heightClampObserver = null;
      }
      if (this.heightClampMql && this.applyBannerHeightClamp) {
        this.heightClampMql.removeEventListener("change", this.applyBannerHeightClamp);
        this.heightClampMql = null;
        this.applyBannerHeightClamp = null;
      }
    }

    init() {
      const swiperContainer = this.closest(".swiper");
      if (!swiperContainer || swiperContainer.classList.contains("swiper-initialized")) return;

      const slides = this.querySelectorAll(".swiper-slide");
      // Defensive: only init if >1 slides
      if (slides.length <= 1) return;

      const slideGapParsed = Number.parseInt(this.dataset.slideGap, 10);
      const gap = Number.isFinite(slideGapParsed) && slideGapParsed >= 0 ? slideGapParsed : 24;

      let breakpoints;
      breakpoints = {
        360: {
          slidesPerView: 2,
          spaceBetween: gap,
        },
        480: {
          slidesPerView: 2,
          spaceBetween: gap,
        },
        600: {
          slidesPerView: 3.5,
          spaceBetween: gap,
        },
        750: {
          slidesPerView: 2.5,
          spaceBetween: gap,
        },
        850: {
          slidesPerView: 3.2,
          spaceBetween: gap,
        },
        1024: {
          slidesPerView: 3.2,
          spaceBetween: gap,
        },
        1200: {
          slidesPerView: 4.2,
          spaceBetween: gap,
        },
        1600: {
          slidesPerView: 5,
          spaceBetween: gap,
        },
      };

      const root = this.closest(".sb-featured-products");

      // Configure Swiper options
      const swiperOptions = {
        slidesPerView: 5,
        spaceBetween: gap,
        breakpoints: breakpoints,
        watchOverflow: false,
        grabCursor: false,
        loop: false,
        navigation: {
          nextEl: (root || swiperContainer).querySelector(".custom-next"),
          prevEl: (root || swiperContainer).querySelector(".custom-prev"),
        },
      };

      this.swiper = new window.Swiper(swiperContainer, swiperOptions);

      swiperContainer.classList.add("swiper-initialized");
      if (root) root.classList.add("is-carousel-initialized");
    }

    renderProducts(products_handle) {
      let completed = 0;
      const total = products_handle.length;
      products_handle.forEach((handle) => {
        this.renderProductDom(handle, () => {
          completed++;
          if (completed === total) {
            if (this.enableSlider) {
              this.init();
            }
            const wrapper = this.closest(".sb-featured-products__cards-wrapper");
            if (wrapper) {
              wrapper.classList.remove("rebuy-enabled");
              wrapper.querySelector(".skeleton-image").classList.add("hidden");
            }
          }
        });
      });
    }

    renderProductDom(handle, callback) {
      const sectionId = this.rebuyProductType === "cart_page" ? "sb-rebuy-product-cart" : "sb-rebuy-product";
      fetch(`${window.origin}/products/${handle}?section_id=${sectionId}`)
        .then((response) => response.text())
        .then((data) => {
          const productDom = new DOMParser().parseFromString(data, "text/html");
          if (productDom) {
            const cardWrapper = productDom.querySelector(".sb-featured-products__card-wrapper");
            if (cardWrapper) {
              const rebuyEndpointId =
                this.rebuyProductType === "custom_endpoint" && this.rebuyCustomEndpointId
                  ? this.rebuyCustomEndpointId
                  : this.rebuyProductType;

              const productLinks = cardWrapper.querySelectorAll("a");
              productLinks.forEach((link) => {
                const url = new URL(link.href, window.location.origin);
                url.searchParams.delete("utm_campaign");
                url.searchParams.set("utm_campaign", `carousel_${rebuyEndpointId}`);
                link.href = url.toString();
              });

              this.appendChild(cardWrapper);
            }
          }
          callback();
        })
        .catch((error) => {
          console.error("Error rendering product DOM:", error);
          callback();
        });
    }

    destroy() {
      if (this.swiper && this.swiper.destroy) {
        this.swiper.destroy();
        this.swiper = null;
        const swiperContainer = this.closest(".swiper");
        if (swiperContainer) {
          swiperContainer.classList.remove("swiper-initialized");
        }
        const root = this.closest(".sb-featured-products");
        if (root) root.classList.remove("is-carousel-initialized");
      }
    }
  }

  customElements.define("sb-featured-products", SBFeaturedProducts);
}
